/**
 * Single source of truth for Job status badge labels/colors, shared by the Jobs
 * list, the Job Detail cards and the JobCalendar popover.
 *
 * Values are the FE-side keys produced by Jobs/utils/adaptJobs — not raw
 * backend statuses. "Needs Attention" membership is decided server-side; the
 * client never re-derives it.
 */

export const DOMAINS = {
  INSPECTION: "inspection",
  CONTRACT: "contract",
  PAYMENT: "payment",
  EMAIL: "email",
};

// `tone` names a colour pair defined once in src/index.css
// (--job-status-<tone>-bg / -fg) and applied by a class in jobStatusBadge.css.
// Inspection tones are the saturated legend colours the JobCalendar popover
// already uses, so a job reads the same on both surfaces.
export const INSPECTION = {
  booked: { label: "Booked", tone: "booked" },
  "pre-inspection": { label: "Pre-inspection", tone: "pre-inspection" },
  "under-inspection": { label: "Under Inspection", tone: "under-inspection" },
  completed: { label: "Completed", tone: "completed" },
};

// Contract / payment / email share the soft subtle-badge scale (Figma 11698-5835).
export const CONTRACT = {
  no_contract: { label: "No Contract", tone: "neutral" },
  // Same label, danger tone: the client is holding a signed agreement that no
  // longer binds (a reset, or a void of an already-completed contract), so the
  // job is actively unprotected until it is re-issued.
  reissue_required: { label: "No Contract", tone: "danger" },
  unsigned: { label: "Unsigned", tone: "warning" },
  signed: { label: "Signed", tone: "success" },
  revoked: { label: "Revoked", tone: "muted" },
  // Both are dead ends that need a brand-new contract, so both read as danger.
  // The labels differ because the fix differs: a decline needs a conversation,
  // a bounce needs a corrected email address.
  declined: { label: "Declined", tone: "danger" },
  delivery_failed: { label: "Delivery Failed", tone: "danger" },
};

// Refund/dispute chips are taken from the payment-management prototype
// (payment-card-variants.html): both terminal outcomes read neutral, an
// unanswered dispute reads danger, and a dispute whose evidence is already in
// reads warning.
export const PAYMENT = {
  no_payment: { label: "No Payment", tone: "neutral" },
  unpaid: { label: "Unpaid", tone: "warning" },
  paid_manual: { label: "Paid (Manual)", tone: "success" },
  paid_stripe: { label: "Paid (Stripe)", tone: "success" },
  refunded: { label: "Refunded", tone: "neutral" },
  disputed: { label: "Disputed", tone: "danger-strong" },
  disputed_review: { label: "Disputed (Under Review)", tone: "warning" },
  dispute_lost: { label: "Dispute Lost", tone: "neutral" },
};

// Figma node 11698-5870.
export const EMAIL_STEP = {
  ready_to_send: { label: "Ready to Send", tone: "info" },
  failed: { label: "Failed", tone: "danger-strong" },
  canceled: { label: "Canceled", tone: "neutral" },
  pending: { label: "Pending", tone: "neutral" },
  sent: { label: "Sent", tone: "success" },
};

const MAPS = {
  [DOMAINS.INSPECTION]: INSPECTION,
  [DOMAINS.CONTRACT]: CONTRACT,
  [DOMAINS.PAYMENT]: PAYMENT,
  [DOMAINS.EMAIL]: EMAIL_STEP,
};

/**
 * @returns {{label: string, tone: string} | null} null when the domain or
 * value is unknown, which the badge renders as an em dash.
 */
export const getStatusInfo = (domain, value) => {
  if (!value) return null;
  const map = MAPS[domain];
  if (!map) return null;
  return map[value] || null;
};
